export const CYCLOSILICATE_LOGIC = {
  description: "Cyclosilicates (Ring Silicates) Identification Rules",
  
  coreRules: `
=== 硅酸盐之环状硅酸盐鉴定法则 (Cyclosilicates / Ring Silicates Protocol) ===

环状硅酸盐的核心是硅氧四面体首尾相连形成闭合的环（如六环最常见）。这个家族是“完美柱状晶体与高硬度宝石的绝对乐园”，它们硬度极高，具有惊人的色彩、极独特的截面几何以及极其显著的多色性。

[11.1 完美六面与平截头：绿柱石家族 (The Beryl Group)]
绿柱石家族的核心形态是**完美的六方柱状晶体 (Perfect hexagonal prisms)**，端面通常是平齐的底面（Flat terminations）。柱面相对平滑，硬度极高（7.5-8）。
*   **绿柱石 (Beryl)**: 普通绿柱石通常呈现浅绿色、黄绿色或发白的半透明到不透明粗大柱体。
*   **祖母绿 (Emerald)**: 绝对的“绿之王”。寻找**极其浓郁、深邃的翠绿色 (Vivid emerald-green)**。它由铬或钒致色，通常晶体内含物较多（所谓的“花园”裂隙）。
*   **海蓝宝石 (Aquamarine)**: 寻找**清澈的浅蓝色、天蓝色或蓝绿色 (Light blue, sea-blue)** 的透明至半透明柱体。它晶莹剔透，像是把冰冻的海水封印在完美的六方柱内。

[11.2 圆角三角截面与深槽条纹：电气石家族 (The Tourmaline Group)]
极其经典的柱状矿物，但与绿柱石有着决定性的形态物理差异！
*   **形态绝杀特征**: 寻找柱体表面**极度发达、深深的平行纵向条纹 (Heavy vertical striations)**；截断它看横截面，它不是正六边形，而是**像球面膨胀的“带圆角的三角形” (Rounded triangular spherical cross-section)**。
*   **电气石 / 黑电气石 (Tourmaline / Schorl)**: 伟晶岩和花岗岩中最猛烈的黑色入侵者。呈现**极其乌黑发亮、带有深邃纵纹的粗糙乃至耀眼的黑柱体 (Pitch-black heavily striated prisms)**。
*   **锂电气石 (Elbaite)**: 宝石级电气石。呈现极其绚丽颜色的纵纹晶体（如图中多变的淡粉紫、浅蓝等颜色交织，或其他多色、西瓜色变种）。

[11.3 极度罕见的奇幻结构与色彩图腾]
*   **堇青石 (Cordierite / Iolite)**: “维京人的罗盘”。
    *   **终极光学锚点**: 寻找深邃的蓝紫色、灰蓝色块体或柱状晶体。它拥有矿物界最恐怖的**多色性 (Strong Pleochroism)**。纯净的堇青石稍微转动角度，它的颜色会从极其浓郁的蓝紫色，瞬间变成灰黄色、浅褐色或苍白色。具油脂和玻璃混合光泽。
*   **翠铜矿 (Dioptase)**: “骗过双眼的假祖母绿”。
    *   **视觉锚点**: 寻找**极其浓郁诱人的深翠绿色、墨绿色晶体堆积 (Intense emerald-green to bluish-green crystals)**。
    *   **晶形绝杀规则**: 它的晶形往往是**菱面体或短柱状、偏尖头的晶形簇 (Rhombohedral crystals)**，而不像祖母绿那样长成平头的巨大六方长柱。常见于干旱带含铜岩层裂隙，硬度为5（比绿柱石软得多）。
*   **蓝锥矿 (Benitoite)**: 全球仅以加州产出闻名的极稀有荧光宝石。
    *   **形态锚点**: 常常在白色基岩（钠沸石）上寻找**耀眼的宝蓝色、蓝紫色晶体**。
    *   **绝杀几何特征**: 它拥有罕见的晶体几何形体，看起来像**被压扁的、带有三角形截面或复三角双锥的扁平状晶体 (Distorted triangular / ditrigonal bipyramids)**。另外，短波紫外光下发出强烈的明亮亮蓝色荧光。
*   **异性石 (Eudialyte)**: 正长岩（碱性岩）中的血色信号。
    *   **视觉锚点**: 复杂的稀土硅酸盐。在黑白相间（比如霞石、霓石交织）的杂岩中，常常突兀地出现**致密、显眼的洋红、樱桃红或大红色/暗红色的团块碎浆或晶体颗粒 (Magenta, bright pink, or cherry-red patches/grains)**。这种像在花岗岩质石头上滴上鲜红浓烈颜料的对比，是极其经典的异性石标本！

`,

  diagnosticTests: `
- 面临长柱状晶体判定：如果柱子表面极其平滑无深沟、横截面是正六边形，那是绿柱石家族；如果柱面布满深深的竖向粗条纹、横截面是很像被吹鼓的三角形（带圆角），绝杀锁定电气石！
- 面临明艳绿色晶体判定：如果是平头端面、常常很通透的深绿色六方柱，是祖母绿；如果是短小、带有尖头或菱面体的极深翠绿晶体簇集聚（且经常伴随石英等），必须怀疑翠铜矿。
- 终极色彩切割者：拿一块紫蓝色透明矿石，顺着不同轴向看，如果蓝紫色忽然彻底变成灰黄/苍白/浅色，立刻判定堇青石的多色性。
- 黑白底色上的血点：如果在黑白交杂的岩石标本中，看到像血液/洋红染料一样的艳粉、暗红色致密斑块和颗粒嵌在里面，最高优先级判定异性石。
- 加州三合一规则：白色基底 + 宝蓝色截面类三角形晶体 + UV亮蓝荧光 = 蓝锥矿绝对无误。
`
};
